<template>
  <div class="min-h-screen bg-bochas-bg">
    <!-- Hero (estilo home: navbar transparente sobre el hero, fondo llega a top:0) -->
    <ImageBackgroundSection
      size="large"
      align="center"
      seed="referentes-hero"
      :image-url="heroImageUrl"
      :loading-image="heroImageLoading"
      alt="Jugadores Históricos"
      top-icon="trofeo"
      top-label="Nuestra historia"
      :title="heroMedia?.title || 'Jugadores Históricos'"
      :subtitle="heroMedia?.subtitle || 'Los referentes que hicieron grande a las bochas de Córdoba'"
    />

    <!-- Loading -->
    <section v-if="loading" class="max-w-7xl mx-auto px-4 py-12 text-center">
      <div
        class="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin"
      ></div>
    </section>

    <!-- Error -->
    <section v-else-if="error" class="max-w-7xl mx-auto px-4 py-12">
      <div
        class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 rounded-lg p-4"
      >
        {{ error }}
      </div>
    </section>

    <!-- Empty -->
    <section
      v-else-if="referentes.length === 0"
      class="max-w-7xl mx-auto px-4 py-12 text-center"
    >
      <p class="text-gray-400 text-base sm:text-lg">
        Aún no hay jugadores históricos publicados.
      </p>
    </section>

    <!-- Listado -->
    <section
      v-else
      class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-24"
    >
      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6"
      >
        <RevealOnScroll
          v-for="(r, index) in referentes"
          :key="r.id"
          as="article"
          :delay="(index % 3) * 100"
          class="card-fbochas overflow-hidden flex flex-col transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-bochas-rojo/30 active:scale-[0.98]"
        >
          <router-link
            :to="`/jugadores-históricos/${r.id}?from=referentes`"
            class="flex flex-col h-full"
          >
            <div class="h-[300px] lg:h-[250px] overflow-hidden bg-bochas-bg">
              <img
                v-if="r.photo_url"
                :src="r.photo_url"
                :alt="r.name"
                class="listing-motion-image w-full h-full object-cover"
                loading="lazy"
              />
              <div
                v-else
                class="w-full h-full bg-degradado-bochas flex items-center justify-center"
              >
                <span class="text-white text-4xl sm:text-5xl font-bold">
                  {{ initials(r) }}
                </span>
              </div>
            </div>

            <div class="p-4 sm:p-5 flex-1 flex flex-col text-center">
              <h2
                class="text-lg sm:text-xl font-bold text-white mb-1 line-clamp-2"
              >
                {{ r.name }}
              </h2>
              <p
                v-if="r.careerStartDate || r.careerEndDate"
                class="text-xs sm:text-sm text-bochas-text-muted mb-2"
              >
                {{ careerPeriod(r) }}
              </p>
              <p
                v-if="r.shortBio || r.bio"
                class="text-xs sm:text-sm text-gray-300 line-clamp-2 flex-1"
              >
                {{ textPreview(r.shortBio || r.bio) }}
              </p>
              <span
                class="mt-3 text-bochas-rojo text-xs sm:text-sm font-semibold"
              >
                Conocer más →
              </span>
            </div>
          </router-link>
        </RevealOnScroll>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { listarReferentes } from "@/services/referentes/referentesService";
import ImageBackgroundSection from "@/components/Common/ImageBackgroundSection.vue";
import RevealOnScroll from "@/components/Animations/RevealOnScroll.vue";
import { useSiteMediaImageState } from "@/composables/useSiteMediaImage";
import { careerPeriod } from "@/utils/playerCareer";

const referentes = ref([]);
const loading = ref(true);
const error = ref("");
const { imageUrl: heroImageUrl, item: heroMedia, loading: heroImageLoading } =
  useSiteMediaImageState("pasado.hero");

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const data = await listarReferentes();
    referentes.value = data.items || data || [];
  } catch (err) {
    error.value =
      err.response?.data?.message ||
      "No pudimos cargar los jugadores históricos.";
  } finally {
    loading.value = false;
  }
}

function initials(r) {
  if (!r.name) return "";
  return r.name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
}

function textPreview(text) {
  if (!text) return "";
  const trimmed = text.replace(/<[^>]*>/g, "").trim();
  return trimmed.length > 120 ? trimmed.slice(0, 120) + "…" : trimmed;
}

onMounted(load);
</script>
